import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence } from '../orderSelectors.ts';
import {
  selectImprovementEntityIds,
  selectImprovementsById,
  selectImprovementsEntities,
} from '../propertyDataSelectors/improvementsSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import {
  selectBaseManufacturedCost,
  selectManufacturedCost,
  selectManufacturedCostOutput,
} from './selectManufacturedCost.ts';
import { selectMultiFamilyCost, selectMultiFamilyCostOutput } from './selectMultiFamilyCost.ts';
import { selectSFRCost, selectSFRCostOutput } from './selectSFRCost.ts';

const selectMainStructureCostOutput = createPriceSelector(
  [
    selectImprovementsById,
    selectSFRCostOutput,
    selectMultiFamilyCostOutput,
    selectManufacturedCostOutput,
  ],
  (improvements, sfrCostOutput, multiFamilyCostOutput, manufacturedCostOutput) => {
    switch (improvements?.propertyType) {
      case 'multiFamily':
        return multiFamilyCostOutput;
      case 'manufacturedHome':
        return manufacturedCostOutput;
      default:
        return sfrCostOutput;
    }
  },
);

export const selectSubjectMainStructureCostOutput = createPriceSelector(
  [(state) => state],
  (state) => {
    return selectMainStructureCostOutput(state, 'subject');
  },
);

export const selectSubjectArvMainStructureCostOutput = createPriceSelector(
  [(state) => state],
  (state) => {
    return selectMainStructureCostOutput(state, 'subjectArv');
  },
);

export const selectMainStructureCost = createPriceSelector(
  [selectImprovementsById, selectSFRCost, selectMultiFamilyCost, selectManufacturedCost],
  (improvements, sfrCost, multiFamilyCost, manufacturedCost) => {
    switch (improvements?.propertyType) {
      case 'multiFamily':
        return multiFamilyCost;
      case 'manufacturedHome':
        return manufacturedCost;
      default:
        return sfrCost;
    }
  },
);

export const selectBaseMainStructureCost = createPriceSelector(
  [selectImprovementsById, selectBaseManufacturedCost],
  (improvements, baseManufacturedCost) => {
    switch (improvements?.propertyType) {
      case 'manufacturedHome':
        return baseManufacturedCost;
      default:
        throw new Error(`Base cost for ${improvements?.propertyType} not implemented yet.`);
    }
  },
);

export const selectMainStructureCosts = createSelector(
  [selectImprovementEntityIds, selectImprovementsEntities, (state) => state],
  (improvementEntityIds, improvements, state) => {
    const entries = improvementEntityIds.map((propID) => {
      const improvement = improvements[propID];
      switch (improvement?.propertyType) {
        case 'multiFamily':
          return [propID, selectMultiFamilyCost(state, propID)];
        case 'manufacturedHome':
          return [propID, selectManufacturedCost(state, propID)];
        default:
          return [propID, selectSFRCost(state, propID)];
      }
    });

    return Object.fromEntries(entries);
  },
);
